import { FC } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Divider,
  Drawer,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import LocationAutocomplete from '../../common/LocationAutoComplete';
import { ICreateStoreV2 } from '../../../schemas/apis/interface';

interface StoreDrawerProps {
  open: boolean;
  onClose: () => void;
  store: ICreateStoreV2;
  onStoreChange: (store: ICreateStoreV2) => void;
  onSave: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent) => void;
  brandsList?: any;
  isPending?: boolean;
  handleLocationChange: (location: string) => void;
  locationInput: string;
}

const StoreDrawer: FC<StoreDrawerProps> = ({
  open,
  onClose,
  store,
  onStoreChange,
  onSave,
  brandsList,
  isPending,
  handleLocationChange,
  locationInput,
}) => {
  // Initialize store with default values for our new fields
  // These could be initialized in the parent component as well
  if (store.is_store_closed === undefined) {
    store.is_store_closed = false;
  }
  if (store.ordering_enabled === undefined) {
    store.ordering_enabled = true;
  }

  // Handle brand selection
  const handleBrandSelect = (brandId: string) => {
    if (!store.brand_ids.includes(brandId)) {
      onStoreChange({
        ...store,
        brand_ids: [...store.brand_ids, brandId],
      });
    }
  };

  // Handle brand removal
  const handleBrandRemove = (brandId: string) => {
    onStoreChange({
      ...store,
      brand_ids: store.brand_ids.filter(id => id !== brandId),
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '600px',
          padding: '20px 24px',
          backgroundColor: '#FAFBFD',
          overflow: 'auto',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {/* Close (Cancel) Button */}
        <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': { backgroundColor: '#E8EBF1' },
              transition: 'all 0.2s ease',
            }}
          >
            <X size={18} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: 'custom.textBlue',
              letterSpacing: '-0.2px',
            }}
          >
            Add Outlet
          </Typography>
        </Box>

        {/* Save Button */}
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={store.friendly_name === '' || isPending}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            },
          }}
          onClick={onSave}
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
      </Box>
      <Divider
        sx={{
          borderColor: 'rgba(0, 0, 0, 0.08)',
          mb: 3,
          mx: -3,
        }}
      />

      {/* Outlet Details Section */}
      <Typography
        sx={{
          fontSize: 16,
          color: 'custom.textBlue',
          mb: 2.5,
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        Outlet Details
      </Typography>

      {/* Outlet Details Form */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              minWidth: '100px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Outlet Address
          </Typography>
          <Box sx={{ width: '100%', position: 'relative' }}>
            <LocationAutocomplete
              value={locationInput}
              onChange={handleLocationChange}
              placeholder="Enter outlet address"
              inputStyle={{}}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: '100px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Outlet Alias
          </Typography>
          <TextField
            value={store.friendly_name}
            onChange={e =>
              onStoreChange({ ...store, friendly_name: e.target.value })
            }
            name="friendly_name"
            placeholder="Enter outlet alias"
            fullWidth
            variant="outlined"
            sx={{
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0px 1000px white inset',
                WebkitTextFillColor: 'black',
                transition: 'background-color 5000s ease-in-out 0s',
              },
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  '& fieldset': { borderColor: '#D0D5DD' },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        {/* Available Brands */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography
            sx={{
              minWidth: '110px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Available Brands
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              width: '350px',
              minHeight: '40px',
              bgcolor: 'white',
              p: 1,
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
            }}
          >
            {store.brand_ids.map(brandId => {
              const brand = brandsList?.data?.data.find(
                (b: any) => b.id === brandId
              );
              return (
                brand && (
                  <Chip
                    key={brand.id}
                    label={brand.name}
                    onDelete={() => handleBrandRemove(brand.id)}
                    sx={{ height: '24px', fontSize: '12px' }}
                  />
                )
              );
            })}
          </Box>
          <Select
            variant="outlined"
            size="small"
            onChange={e => handleBrandSelect(e.target.value)}
            value=""
            displayEmpty
            renderValue={() => 'Select brand'}
            sx={{
              minWidth: '116px',
              height: '40px',
              borderRadius: '6px',
              textTransform: 'none',
              color: 'text.secondary',
              borderColor: 'lightGray.main',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              bgcolor: '#F8F9FC',
            }}
          >
            {brandsList?.data?.data.filter(
              (brand: any) => !store.brand_ids.includes(brand.id)
            ).length === 0 ? (
              <MenuItem disabled>No brands</MenuItem>
            ) : (
              brandsList?.data?.data
                .filter((brand: any) => !store.brand_ids.includes(brand.id))
                .map((brand: any) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))
            )}
          </Select>
        </Box>
      </Box>

      <Divider
        sx={{
          borderColor: 'rgba(0, 0, 0, 0.08)',
          mb: 3,
          mx: -3,
        }}
      />

      {/* Outlet Contact Details Section */}
      <Typography
        sx={{
          fontSize: 16,
          color: 'custom.textBlue',
          mb: 2.5,
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        Outlet Contact Details
      </Typography>

      {/* Contact Details with 2 inputs per row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* Row 1: Name and Email */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Name Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography
              sx={{
                minWidth: '45px',
                fontWeight: 500,
                fontSize: 14,
                color: 'lightGray.text',
              }}
            >
              Name
            </Typography>
            <TextField
              value={store.poc_name}
              onChange={e =>
                onStoreChange({ ...store, poc_name: e.target.value })
              }
              name="poc_at_brand"
              placeholder="Enter name"
              fullWidth
              variant="outlined"
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0px 1000px white inset',
                  WebkitTextFillColor: 'black',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    '& fieldset': { borderColor: '#D0D5DD' },
                  },
                  '&.Mui-focused': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 14px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Email Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography
              sx={{
                minWidth: '45px',
                fontWeight: 500,
                fontSize: 14,
                color: 'lightGray.text',
              }}
            >
              Email
            </Typography>
            <TextField
              value={store.poc_email}
              onChange={e =>
                onStoreChange({ ...store, poc_email: e.target.value })
              }
              name="poc_at_email"
              placeholder="example@gmail.com"
              fullWidth
              variant="outlined"
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0px 1000px white inset',
                  WebkitTextFillColor: 'black',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    '& fieldset': { borderColor: '#D0D5DD' },
                  },
                  '&.Mui-focused': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 14px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </Box>

        {/* Row 2: Role and Contact */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Role Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography
              sx={{
                minWidth: '45px',
                fontWeight: 500,
                fontSize: 14,
                color: 'lightGray.text',
              }}
            >
              Role
            </Typography>
            <TextField
              value={store.poc_role}
              onChange={e =>
                onStoreChange({ ...store, poc_role: e.target.value })
              }
              name="poc_role"
              placeholder="Enter role"
              fullWidth
              variant="outlined"
              sx={{
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0px 1000px white inset',
                  WebkitTextFillColor: 'black',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    '& fieldset': { borderColor: '#D0D5DD' },
                  },
                  '&.Mui-focused': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '10px 14px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Contact Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography
              sx={{
                minWidth: '45px',
                fontWeight: 500,
                fontSize: 14,
                color: 'lightGray.text',
              }}
            >
              Contact
            </Typography>
            <Box sx={{ width: '100%' }}>
              <PhoneInput
                country="us"
                value={store.poc_phone}
                onChange={e => onStoreChange({ ...store, poc_phone: e })}
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #E0E0E0',
                  transition: 'all 0.2s ease',
                }}
                buttonStyle={{
                  borderRadius: '6px 0 0 6px',
                  border: '1px solid #E0E0E0',
                  backgroundColor: 'white',
                }}
                inputProps={{ name: 'phone', required: true }}
                containerStyle={{
                  width: '100%',
                  position: 'relative',
                }}
                dropdownStyle={{
                  position: 'absolute',
                  top: 'auto',
                  bottom: '100%',
                  left: 0,
                  zIndex: 9999,
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                  borderRadius: '6px',
                }}
                placeholder="Enter phone number"
                specialLabel=""
                enableSearch={true}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default StoreDrawer;
